import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dshow = (d) => {
  if (!d) return "";
  const [y, m, dd] = d.split("-");
  const M = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${dd}-${M[(+m || 1) - 1]}-${y.slice(2)}`;
};

const Chip = ({ k, label }) => (
  <span className="inline-flex items-center gap-1 mr-3 whitespace-nowrap">
    <kbd className="px-1.5 py-0.5 rounded-sm bg-[#0f766e] text-white text-[10px] font-bold tracking-wide">{k}</kbd>
    <span className="text-[11px] text-slate-600">{label}</span>
  </span>
);
const KeyBar = ({ chips }) => (
  <div className="border-t border-[#e7dcc3] bg-[#f6efdd] px-4 py-2 flex flex-wrap items-center" data-testid="fin-keybar">
    {chips.map(([k, l]) => <Chip key={k + l} k={k} label={l} />)}
  </div>
);
const FnBtn = ({ k, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[12px] font-semibold text-slate-700 hover:bg-[#0f766e] hover:text-white rounded-sm transition-colors">
    <span className="min-w-[42px] inline-block px-1 py-0.5 text-center rounded-sm bg-white/80 border border-[#d8ccae] text-[10px] font-bold text-[#0f766e]">{k}</span>
    {label}
  </button>
);

const Frame = ({ title, sub, right, children, chips, sidebar }) => (
  <div className="flex flex-col h-[calc(100vh-4rem)] bg-[#eef1f4]">
    <div className="bg-[#0f766e] text-white px-4 py-2 flex items-center justify-between">
      <div>
        <div className="text-sm font-bold tracking-wide" data-testid="fin-title">{title}</div>
        <div className="text-[11px] text-teal-100">{sub}</div>
      </div>
      {right}
    </div>
    <div className="flex flex-1 min-h-0">
      <div className="flex-1 min-w-0 flex flex-col bg-white border-r border-[#e7dcc3] overflow-y-auto">{children}</div>
      <div className="w-44 bg-[#f6efdd] border-l border-[#e7dcc3] p-2 space-y-1 hidden md:block">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-1 pb-1">Actions</div>
        {sidebar}
      </div>
    </div>
    <KeyBar chips={chips} />
  </div>
);

async function finExport(report, period, format) {
  try {
    const res = await axios.get(`${API}/tally/fin/export`, {
      params: { report, frm: period.from, to: period.to, format }, responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${report}_${period.to}.${format === "pdf" ? "pdf" : "xlsx"}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(format === "pdf" ? "PDF exported" : "Excel exported");
  } catch { toast.error("Export failed"); }
}

/* ---------- edit-figure dialog (override / AUTO) ---------- */
function EditValueDialog({ label, auto, current, overridden, onClose, onSave }) {
  const [v, setV] = useState(overridden ? String(current) : "");
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const save = useCallback(() => {
    onSave(v.trim() === "" ? null : Number(v));
  }, [v, onSave]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
      if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); save(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, save]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" data-testid="fin-edit-dialog">
      <div className="bg-[#fbf7ee] border-2 border-[#0f766e] shadow-2xl w-96">
        <div className="bg-[#0f766e] text-white px-3 py-1.5 text-xs font-bold tracking-widest uppercase">Edit Figure</div>
        <div className="p-4 space-y-3">
          <div className="text-sm font-bold text-slate-800">{label}</div>
          <div className="text-[11px] text-slate-500">Auto value: {fmt0(auto)} — leave blank &amp; save to revert to AUTO</div>
          <input ref={ref} type="number" step="0.01" value={v} onChange={(e) => setV(e.target.value)}
            data-testid="fin-edit-input" placeholder={`AUTO (${fmt0(auto)})`}
            className="w-full border border-[#d8ccae] rounded-sm px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0f766e]" />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1 text-xs font-bold border border-[#d8ccae] rounded-sm">Esc Cancel</button>
            <button onClick={save} data-testid="fin-edit-save"
              className="px-3 py-1 text-xs font-bold bg-[#0f766e] text-white rounded-sm">Enter Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function useOverrideSaver(stmt, period, reload) {
  return useCallback(async (rowKey, value) => {
    try {
      await axios.put(`${API}/tally/fin/override`, {
        stmt, frm: period.from, to: period.to, row_key: rowKey, value,
      });
      toast.success(value === null ? "Reverted to AUTO" : "Figure updated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  }, [stmt, period, reload]);
}

const AmtCell = ({ r, active }) => (
  <span className="tabular-nums">
    {fmt0(r.amount)}
    {r.overridden && <span className={`ml-1 text-[9px] font-bold ${active ? "text-amber-200" : "text-amber-700"}`}>EDITED</span>}
  </span>
);

function useViewKeys({ blocked, dialog, count, sel, setSel, onEnter, onEsc, onPeriod, onPdf, onXls }) {
  useEffect(() => {
    if (blocked || dialog) return undefined;
    const onKey = (e) => {
      const stop = () => { e.preventDefault(); e.stopPropagation(); };
      if (e.key === "ArrowDown") { stop(); setSel((s) => Math.min(s + 1, count - 1)); }
      else if (e.key === "ArrowUp") { stop(); setSel((s) => Math.max(s - 1, 0)); }
      else if (e.key === "PageDown") { stop(); setSel((s) => Math.min(s + 12, count - 1)); }
      else if (e.key === "PageUp") { stop(); setSel((s) => Math.max(s - 12, 0)); }
      else if (e.key === "Enter") { stop(); onEnter && onEnter(sel); }
      else if (e.key === "Escape") { stop(); onEsc(); }
      else if (e.key === "F2") { stop(); onPeriod(); }
      else if (e.altKey && (e.key === "p" || e.key === "P")) { stop(); onPdf(); }
      else if (e.altKey && (e.key === "e" || e.key === "E")) { stop(); onXls(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [blocked, dialog, count, sel, setSel, onEnter, onEsc, onPeriod, onPdf, onXls]);
}

const STD_CHIPS = [["↑↓", "Move"], ["Enter", "Edit Figure"], ["F2", "Period"], ["Alt+P", "PDF"], ["Alt+E", "Excel"], ["Esc", "Back"]];

/* ================= BALANCE SHEET ================= */
export function BalanceSheetView({ period, onBack, onOpenPeriod, blocked }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(0);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/tally/fin/balance-sheet`, { params: { frm: period.from, to: period.to } })
      .then((r) => setData(r.data)).catch(() => toast.error("Could not load the Balance Sheet"));
  }, [period]);
  useEffect(() => { load(); }, [load]);
  const saveOv = useOverrideSaver("bs", period, load);
  const rows = data ? [...data.liabilities.map((r) => ({ ...r, side: "L" })), ...data.assets.map((r) => ({ ...r, side: "A" }))] : [];
  useViewKeys({
    blocked, dialog: !!edit, count: rows.length, sel, setSel,
    onEnter: (i) => rows[i] && setEdit(rows[i]),
    onEsc: onBack, onPeriod: onOpenPeriod,
    onPdf: () => finExport("bs", period, "pdf"), onXls: () => finExport("bs", period, "xlsx"),
  });
  const Side = ({ list, label, offset }) => (
    <div className="flex-1 min-w-0">
      <div className="px-3 py-1.5 bg-[#f6efdd] text-[11px] font-bold uppercase tracking-widest text-[#0f766e] border-b border-[#eee5cf]">{label}</div>
      {list.map((r, i) => {
        const gi = offset + i;
        const active = sel === gi;
        return (
          <div key={r.key} data-testid={`bs-row-${r.key}`} onClick={() => setSel(gi)} onDoubleClick={() => setEdit(rows[gi])}
            onMouseEnter={() => setSel(gi)}
            className={`flex justify-between px-3 py-1.5 text-[13px] cursor-pointer border-b border-[#f7f2e4] ${active ? "bg-[#0f766e] text-white" : "hover:bg-[#f6efdd] text-slate-800"}`}>
            <span className="font-medium">{r.label}</span>
            <AmtCell r={r} active={active} />
          </div>
        );
      })}
    </div>
  );
  return (
    <Frame title="Balance Sheet" sub={`As on ${dshow(period.to)} · Period ${dshow(period.from)} to ${dshow(period.to)}`}
      chips={STD_CHIPS}
      sidebar={<>
        <FnBtn k="F2" label="Period" onClick={onOpenPeriod} testid="bs-fn-period" />
        <FnBtn k="Alt+P" label="Export PDF" onClick={() => finExport("bs", period, "pdf")} testid="bs-export-pdf" />
        <FnBtn k="Alt+E" label="Export Excel" onClick={() => finExport("bs", period, "xlsx")} testid="bs-export-excel" />
        <FnBtn k="Esc" label="Back" onClick={onBack} testid="bs-fn-back" />
      </>}>
      <div data-testid="fin-balance-sheet" className="flex flex-col flex-1">
        {data && (
          <>
            <div className="flex flex-1">
              <Side list={data.liabilities} label="Liabilities" offset={0} />
              <div className="w-px bg-[#e7dcc3]" />
              <Side list={data.assets} label="Assets" offset={data.liabilities.length} />
            </div>
            <div className="border-t-2 border-[#0f766e] bg-[#fbf7ee] flex text-[13px] font-bold text-slate-800">
              <div className="flex-1 flex justify-between px-3 py-1.5"><span>Total</span><span className="tabular-nums" data-testid="bs-total-liab">{fmt0(data.totals.liabilities)}</span></div>
              <div className="w-px bg-[#e7dcc3]" />
              <div className="flex-1 flex justify-between px-3 py-1.5"><span>Total</span><span className="tabular-nums" data-testid="bs-total-assets">{fmt0(data.totals.assets)}</span></div>
            </div>
            {Math.abs(data.totals.difference) > 0.01 && (
              <div className="px-3 py-1 text-[11px] font-bold text-amber-800 bg-amber-50 border-t border-amber-200" data-testid="bs-difference">
                Difference (Assets − Liabilities): {fmt0(data.totals.difference)} — edit Capital / Fixed Assets / Loans to balance
              </div>
            )}
          </>
        )}
      </div>
      {edit && (
        <EditValueDialog label={edit.label} auto={edit.auto} current={edit.amount} overridden={edit.overridden}
          onClose={() => setEdit(null)} onSave={(v) => { setEdit(null); saveOv(edit.key, v); }} />
      )}
    </Frame>
  );
}

/* ================= PROFIT & LOSS ================= */
export function ProfitLossView({ period, onBack, onOpenPeriod, blocked }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(0);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/tally/fin/profit-loss`, { params: { frm: period.from, to: period.to } })
      .then((r) => setData(r.data)).catch(() => toast.error("Could not load the P&L"));
  }, [period]);
  useEffect(() => { load(); }, [load]);
  const saveOv = useOverrideSaver("pl", period, load);
  const editable = data ? [...data.tier1.debit, ...data.tier1.credit, ...data.tier2.debit, ...data.tier2.credit] : [];
  useViewKeys({
    blocked, dialog: !!edit, count: editable.length, sel, setSel,
    onEnter: (i) => editable[i] && setEdit(editable[i]),
    onEsc: onBack, onPeriod: onOpenPeriod,
    onPdf: () => finExport("pl", period, "pdf"), onXls: () => finExport("pl", period, "xlsx"),
  });
  const idxOf = (r) => editable.findIndex((x) => x.key === r.key);
  const Row = ({ r, computed }) => {
    const gi = computed ? -1 : idxOf(r);
    const active = gi >= 0 && sel === gi;
    return (
      <div data-testid={`pl-row-${r.key || r.label}`}
        onClick={() => gi >= 0 && setSel(gi)} onDoubleClick={() => gi >= 0 && setEdit(r)}
        onMouseEnter={() => gi >= 0 && setSel(gi)}
        className={`flex justify-between px-3 py-1.5 text-[13px] border-b border-[#f7f2e4] ${computed ? "italic font-bold text-slate-700 bg-[#fbf7ee]" : "cursor-pointer"} ${active ? "bg-[#0f766e] text-white" : !computed ? "hover:bg-[#f6efdd] text-slate-800" : ""}`}>
        <span className="font-medium">{r.label}</span>
        {computed ? <span className="tabular-nums">{fmt0(r.amount)}</span> : <AmtCell r={r} active={active} />}
      </div>
    );
  };
  const gp = data?.gross_profit ?? 0;
  const np = data?.net_profit ?? 0;
  return (
    <Frame title="Profit & Loss A/c" sub={`${dshow(period.from)} to ${dshow(period.to)}`}
      chips={STD_CHIPS}
      sidebar={<>
        <FnBtn k="F2" label="Period" onClick={onOpenPeriod} testid="pl-fn-period" />
        <FnBtn k="Alt+P" label="Export PDF" onClick={() => finExport("pl", period, "pdf")} testid="pl-export-pdf" />
        <FnBtn k="Alt+E" label="Export Excel" onClick={() => finExport("pl", period, "xlsx")} testid="pl-export-excel" />
        <FnBtn k="Esc" label="Back" onClick={onBack} testid="pl-fn-back" />
      </>}>
      <div data-testid="fin-profit-loss" className="flex flex-col flex-1">
        {data && (
          <>
            <div className="flex">
              <div className="flex-1 min-w-0">
                <div className="px-3 py-1.5 bg-[#f6efdd] text-[11px] font-bold uppercase tracking-widest text-[#0f766e] border-b border-[#eee5cf]">Particulars (Dr)</div>
                {data.tier1.debit.map((r) => <Row key={r.key} r={r} />)}
                {gp >= 0 && <Row r={{ label: "Gross Profit c/o", amount: gp }} computed />}
              </div>
              <div className="w-px bg-[#e7dcc3]" />
              <div className="flex-1 min-w-0">
                <div className="px-3 py-1.5 bg-[#f6efdd] text-[11px] font-bold uppercase tracking-widest text-[#0f766e] border-b border-[#eee5cf]">Particulars (Cr)</div>
                {data.tier1.credit.map((r) => <Row key={r.key} r={r} />)}
                {gp < 0 && <Row r={{ label: "Gross Loss c/o", amount: -gp }} computed />}
              </div>
            </div>
            <div className="border-y-2 border-[#0f766e] bg-[#fbf7ee] flex text-[13px] font-bold text-slate-800">
              <div className="flex-1 flex justify-between px-3 py-1"><span>Total</span><span className="tabular-nums" data-testid="pl-t1-total">{fmt0(data.tier1.total)}</span></div>
              <div className="w-px bg-[#e7dcc3]" />
              <div className="flex-1 flex justify-between px-3 py-1"><span>Total</span><span className="tabular-nums">{fmt0(data.tier1.total)}</span></div>
            </div>
            <div className="flex flex-1">
              <div className="flex-1 min-w-0">
                {data.tier2.debit.map((r) => <Row key={r.key} r={r} />)}
                {np >= 0 && <Row r={{ label: "Nett Profit", amount: np }} computed />}
              </div>
              <div className="w-px bg-[#e7dcc3]" />
              <div className="flex-1 min-w-0">
                {gp >= 0 && <Row r={{ label: "Gross Profit b/f", amount: gp }} computed />}
                {data.tier2.credit.map((r) => <Row key={r.key} r={r} />)}
                {np < 0 && <Row r={{ label: "Nett Loss", amount: -np }} computed />}
              </div>
            </div>
            <div className="border-t-2 border-[#0f766e] bg-[#fbf7ee] flex text-[13px] font-bold text-slate-800">
              <div className="flex-1 flex justify-between px-3 py-1.5"><span>Total</span><span className="tabular-nums" data-testid="pl-t2-total">{fmt0(Math.max(data.tier2.total_debit, data.tier2.total_credit))}</span></div>
              <div className="w-px bg-[#e7dcc3]" />
              <div className="flex-1 flex justify-between px-3 py-1.5"><span>Total</span><span className="tabular-nums">{fmt0(Math.max(data.tier2.total_debit, data.tier2.total_credit))}</span></div>
            </div>
            <div className="px-3 py-1 text-[11px] font-semibold text-slate-500">
              Gross Profit: {fmt0(gp)} · Nett Profit: <span data-testid="pl-net-profit" className={np >= 0 ? "text-emerald-700 font-bold" : "text-red-700 font-bold"}>{fmt0(np)}</span>
            </div>
          </>
        )}
      </div>
      {edit && (
        <EditValueDialog label={edit.label} auto={edit.auto} current={edit.amount} overridden={edit.overridden}
          onClose={() => setEdit(null)} onSave={(v) => { setEdit(null); saveOv(edit.key, v); }} />
      )}
    </Frame>
  );
}

/* ================= STOCK SUMMARY ================= */
export function StockSummaryView({ period, onBack, onOpenPeriod, blocked }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(0);
  const [item, setItem] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/tally/fin/stock-summary`, { params: { frm: period.from, to: period.to } })
      .then((r) => setData(r.data)).catch(() => toast.error("Could not load Stock Summary"));
  }, [period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!item) { setDetail(null); return; }
    axios.get(`${API}/tally/fin/stock-item`, { params: { item, frm: period.from, to: period.to } })
      .then((r) => setDetail(r.data)).catch(() => toast.error("Could not load the item"));
  }, [item, period]);
  const rows = item ? (detail?.rows || []) : (data?.rows || []);
  useViewKeys({
    blocked, dialog: false, count: rows.length, sel, setSel,
    onEnter: (i) => { if (!item && data?.rows?.[i]) { setItem(data.rows[i].name); setSel(0); } },
    onEsc: () => { if (item) { setItem(null); setSel(0); } else onBack(); },
    onPeriod: onOpenPeriod,
    onPdf: () => finExport("stock", period, "pdf"), onXls: () => finExport("stock", period, "xlsx"),
  });
  const th = "px-2 py-1.5 font-bold text-left";
  const thr = "px-2 py-1.5 font-bold text-right";
  return (
    <Frame title={item ? `Stock Item: ${item}` : "Stock Summary"} sub={`${dshow(period.from)} to ${dshow(period.to)}`}
      chips={[["↑↓", "Move"], ["Enter", "Open Item"], ["F2", "Period"], ["Alt+P", "PDF"], ["Alt+E", "Excel"], ["Esc", item ? "Back to Summary" : "Back"]]}
      sidebar={<>
        <FnBtn k="F2" label="Period" onClick={onOpenPeriod} testid="ss-fn-period" />
        <FnBtn k="Alt+P" label="Export PDF" onClick={() => finExport("stock", period, "pdf")} testid="ss-export-pdf" />
        <FnBtn k="Alt+E" label="Export Excel" onClick={() => finExport("stock", period, "xlsx")} testid="ss-export-excel" />
        <FnBtn k="Esc" label="Back" onClick={() => (item ? setItem(null) : onBack())} testid="ss-fn-back" />
      </>}>
      {!item ? (
        <table className="w-full text-[12.5px]" data-testid="fin-stock-summary">
          <thead><tr className="bg-[#f6efdd] text-slate-700">
            <th className={th}>Item</th><th className={th}>Unit</th>
            <th className={thr}>Opening Qty</th><th className={thr}>Inward Qty</th><th className={thr}>Inward Value</th>
            <th className={thr}>Outward Qty</th><th className={thr}>Outward Value</th>
            <th className={thr}>Closing Qty</th><th className={thr}>Closing Value</th>
          </tr></thead>
          <tbody>
            {(data?.rows || []).map((r, i) => (
              <tr key={r.name} data-testid={`ss-row-${i}`} onClick={() => setSel(i)}
                onDoubleClick={() => { setItem(r.name); setSel(0); }} onMouseEnter={() => setSel(i)}
                className={`border-b border-[#f1ecdd] cursor-pointer ${sel === i ? "bg-[#0f766e] text-white" : "hover:bg-[#f6efdd]"}`}>
                <td className="px-2 py-1 font-medium">{r.name}</td><td className="px-2 py-1">{r.unit}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmt0(r.open_q)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmt0(r.in_q)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmt0(r.in_v)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmt0(r.out_q)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmt0(r.out_v)}</td>
                <td className={`px-2 py-1 text-right tabular-nums font-bold ${sel === i ? "" : r.close_q < 0 ? "text-red-700" : "text-emerald-800"}`}>{fmt0(r.close_q)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmt0(r.close_v)}</td>
              </tr>
            ))}
          </tbody>
          {data && (
            <tfoot><tr className="bg-[#fbf7ee] font-bold border-t-2 border-[#0f766e]">
              <td className="px-2 py-1.5" colSpan={2}>Grand Total</td>
              <td className="px-2 py-1.5 text-right">{fmt0(data.grand_total.open_q)}</td>
              <td className="px-2 py-1.5 text-right">{fmt0(data.grand_total.in_q)}</td>
              <td className="px-2 py-1.5 text-right">{fmt0(data.grand_total.in_v)}</td>
              <td className="px-2 py-1.5 text-right">{fmt0(data.grand_total.out_q)}</td>
              <td className="px-2 py-1.5 text-right">{fmt0(data.grand_total.out_v)}</td>
              <td className="px-2 py-1.5 text-right" data-testid="ss-total-close-q">{fmt0(data.grand_total.close_q)}</td>
              <td className="px-2 py-1.5 text-right">{fmt0(data.grand_total.close_v)}</td>
            </tr></tfoot>
          )}
        </table>
      ) : (
        <table className="w-full text-[12.5px]" data-testid="fin-stock-item">
          <thead><tr className="bg-[#f6efdd] text-slate-700">
            <th className={th}>Date</th><th className={th}>Party</th><th className={th}>Vch Type</th><th className={th}>Vch No.</th>
            <th className={thr}>Inward</th><th className={thr}>Outward</th><th className={thr}>Value</th><th className={thr}>Balance Qty</th>
          </tr></thead>
          <tbody>
            <tr className="bg-[#fbf7ee] border-b border-[#eee5cf]">
              <td className="px-2 py-1" /><td className="px-2 py-1 italic font-bold text-slate-600">Opening Balance</td>
              <td colSpan={5} /><td className="px-2 py-1 text-right font-bold" data-testid="si-opening">{fmt0(detail?.opening_q)}</td>
            </tr>
            {(detail?.rows || []).map((r, i) => (
              <tr key={i} data-testid={`si-row-${i}`} onMouseEnter={() => setSel(i)}
                className={`border-b border-[#f1ecdd] ${sel === i ? "bg-[#0f766e] text-white" : "hover:bg-[#f6efdd]"}`}>
                <td className="px-2 py-1">{dshow(r.date)}</td><td className="px-2 py-1">{r.party}</td>
                <td className="px-2 py-1">{r.vch_type}</td><td className="px-2 py-1">{r.vch_no}</td>
                <td className="px-2 py-1 text-right tabular-nums">{r.in_q ? fmt0(r.in_q) : ""}</td>
                <td className="px-2 py-1 text-right tabular-nums">{r.out_q ? fmt0(r.out_q) : ""}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmt0(r.value)}</td>
                <td className="px-2 py-1 text-right tabular-nums font-bold">{fmt0(r.balance_q)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="bg-[#fbf7ee] font-bold border-t-2 border-[#0f766e]">
            <td className="px-2 py-1.5" colSpan={7}>Closing Quantity</td>
            <td className="px-2 py-1.5 text-right" data-testid="si-closing">{fmt0(detail?.closing_q)}</td>
          </tr></tfoot>
        </table>
      )}
    </Frame>
  );
}

/* ================= BANK BOOK / STATEMENT ================= */
function BankEditDialog({ row, onClose, onSaved }) {
  const [party, setParty] = useState(row.party);
  const [desc, setDesc] = useState(row.description);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const save = useCallback(async () => {
    try {
      await axios.put(`${API}/tally/fin/bank-txn/${row.id}`, { party_name: party, description: desc });
      toast.success("Entry updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  }, [row.id, party, desc, onSaved]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
      if (e.key === "Enter" && (e.ctrlKey || e.altKey)) { e.preventDefault(); e.stopPropagation(); save(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, save]);
  const inp = "mt-1 w-full border border-[#d8ccae] rounded-sm px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0f766e]";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" data-testid="bank-edit-dialog">
      <div className="bg-[#fbf7ee] border-2 border-[#0f766e] shadow-2xl w-[28rem]">
        <div className="bg-[#0f766e] text-white px-3 py-1.5 text-xs font-bold tracking-widest uppercase">Edit Bank Entry — {dshow(row.date)}</div>
        <div className="p-4 space-y-3">
          <label className="block text-xs font-semibold text-slate-600">Party
            <input ref={ref} value={party} onChange={(e) => setParty(e.target.value)} data-testid="bank-edit-party" className={inp} />
          </label>
          <label className="block text-xs font-semibold text-slate-600">Narration / Description
            <input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="bank-edit-desc" className={inp} />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1 text-xs font-bold border border-[#d8ccae] rounded-sm">Esc Cancel</button>
            <button onClick={save} data-testid="bank-edit-save" className="px-3 py-1 text-xs font-bold bg-[#0f766e] text-white rounded-sm">Ctrl+Enter Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function BankBookView({ period, onBack, onOpenPeriod, blocked }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(0);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/tally/fin/bank-book`, { params: { frm: period.from, to: period.to } })
      .then((r) => setData(r.data)).catch(() => toast.error("Could not load the Bank Statement"));
  }, [period]);
  useEffect(() => { load(); }, [load]);
  const rows = data?.rows || [];
  useViewKeys({
    blocked, dialog: !!edit, count: rows.length, sel, setSel,
    onEnter: (i) => rows[i] && setEdit(rows[i]),
    onEsc: onBack, onPeriod: onOpenPeriod,
    onPdf: () => finExport("bank", period, "pdf"), onXls: () => finExport("bank", period, "xlsx"),
  });
  const th = "px-2 py-1.5 font-bold text-left";
  const thr = "px-2 py-1.5 font-bold text-right";
  return (
    <Frame title="Bank Statement" sub={`${dshow(period.from)} to ${dshow(period.to)} · Opening ${fmt0(data?.opening)} · Closing ${fmt0(data?.closing)}`}
      chips={[["↑↓", "Move"], ["Enter", "Edit Entry"], ["F2", "Period"], ["Alt+P", "PDF"], ["Alt+E", "Excel"], ["Esc", "Back"]]}
      sidebar={<>
        <FnBtn k="F2" label="Period" onClick={onOpenPeriod} testid="bb-fn-period" />
        <FnBtn k="Alt+P" label="Export PDF" onClick={() => finExport("bank", period, "pdf")} testid="bb-export-pdf" />
        <FnBtn k="Alt+E" label="Export Excel" onClick={() => finExport("bank", period, "xlsx")} testid="bb-export-excel" />
        <FnBtn k="Esc" label="Back" onClick={onBack} testid="bb-fn-back" />
      </>}>
      <table className="w-full text-[12.5px]" data-testid="fin-bank-book">
        <thead><tr className="bg-[#f6efdd] text-slate-700">
          <th className={th}>Date</th><th className={th}>Party</th><th className={th}>Description</th>
          <th className={th}>Linked To</th><th className={thr}>Deposit</th><th className={thr}>Withdrawal</th><th className={thr}>Balance</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id + i} data-testid={`bb-row-${i}`} onClick={() => setSel(i)} onDoubleClick={() => setEdit(r)}
              onMouseEnter={() => setSel(i)}
              className={`border-b border-[#f1ecdd] cursor-pointer ${sel === i ? "bg-[#0f766e] text-white" : "hover:bg-[#f6efdd]"}`}>
              <td className="px-2 py-1 whitespace-nowrap">{dshow(r.date)}</td>
              <td className="px-2 py-1 max-w-[160px] truncate font-medium">{r.party}</td>
              <td className="px-2 py-1 max-w-[240px] truncate">{r.description}</td>
              <td className={`px-2 py-1 max-w-[170px] truncate text-[11px] font-semibold ${sel === i ? "text-teal-100" : "text-amber-700"}`}>{r.linked}</td>
              <td className="px-2 py-1 text-right tabular-nums">{r.deposit ? fmt0(r.deposit) : ""}</td>
              <td className="px-2 py-1 text-right tabular-nums">{r.withdrawal ? fmt0(r.withdrawal) : ""}</td>
              <td className="px-2 py-1 text-right tabular-nums font-medium">{fmt0(r.balance)}</td>
            </tr>
          ))}
          {data && rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No bank entries in this period</td></tr>}
        </tbody>
        {data && (
          <tfoot><tr className="bg-[#fbf7ee] font-bold border-t-2 border-[#0f766e]">
            <td className="px-2 py-1.5" colSpan={4}>Total</td>
            <td className="px-2 py-1.5 text-right" data-testid="bb-total-dep">{fmt0(data.totals.deposit)}</td>
            <td className="px-2 py-1.5 text-right" data-testid="bb-total-wd">{fmt0(data.totals.withdrawal)}</td>
            <td className="px-2 py-1.5 text-right" data-testid="bb-closing">{fmt0(data.closing)}</td>
          </tr></tfoot>
        )}
      </table>
      {edit && <BankEditDialog row={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </Frame>
  );
}

/* ================= OPENING BALANCES (Books Beginning) ================= */
function OBDialog({ row, kind, onClose, onSaved }) {
  const isStock = kind === "stock";
  const [amount, setAmount] = useState(row.amount || "");
  const [drcr, setDrcr] = useState(row.drcr || "dr");
  const [qty, setQty] = useState(row.qty || "");
  const [rate, setRate] = useState(row.rate || "");
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const save = useCallback(async () => {
    try {
      await axios.put(`${API}/opening-balances`, {
        kind, key: row.name, drcr,
        amount: Number(amount || 0), qty: Number(qty || 0), rate: Number(rate || 0),
      });
      toast.success("Opening balance saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  }, [kind, row.name, drcr, amount, qty, rate, onSaved]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
      if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); save(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, save]);
  const inp = "mt-1 w-full border border-[#d8ccae] rounded-sm px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0f766e]";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" data-testid="ob-dialog">
      <div className="bg-[#fbf7ee] border-2 border-[#0f766e] shadow-2xl w-96">
        <div className="bg-[#0f766e] text-white px-3 py-1.5 text-xs font-bold tracking-widest uppercase">Opening Balance — {row.name}</div>
        <div className="p-4 space-y-3">
          {isStock ? (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-semibold text-slate-600">Quantity {row.unit ? `(${row.unit})` : ""}
                <input ref={ref} type="number" step="0.001" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="ob-qty" className={inp} placeholder="0" />
              </label>
              <label className="text-xs font-semibold text-slate-600">Rate / unit
                <input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="ob-rate" className={inp} placeholder="0.00" />
              </label>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-semibold text-slate-600">Amount
                <input ref={ref} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="ob-amount" className={inp} placeholder="0.00 (blank = clear)" />
              </label>
              <label className="text-xs font-semibold text-slate-600">Dr / Cr
                <select value={drcr} onChange={(e) => setDrcr(e.target.value)} data-testid="ob-drcr" className={inp}>
                  <option value="dr">Dr — they owe you / asset</option>
                  <option value="cr">Cr — you owe them</option>
                </select>
              </label>
            </div>
          )}
          <div className="text-[11px] text-slate-500">Set 0 / blank to clear. Applies before your very first entry and rolls forward into every FY automatically.</div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1 text-xs font-bold border border-[#d8ccae] rounded-sm">Esc Cancel</button>
            <button onClick={save} data-testid="ob-save" className="px-3 py-1 text-xs font-bold bg-[#0f766e] text-white rounded-sm">Enter Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function OpeningBalancesView({ onBack, onOpenPeriod, blocked }) {
  const [tab, setTab] = useState("party");
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(0);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => {
    if (tab === "party") {
      axios.get(`${API}/opening-balances/parties`).then((r) => setRows(r.data.parties || []));
    } else if (tab === "stock") {
      axios.get(`${API}/opening-balances/stock-items`).then((r) => setRows(r.data.items || []));
    } else {
      axios.get(`${API}/opening-balances`).then((r) => {
        const obs = r.data.items || [];
        const get = (k) => obs.find((o) => o.kind === k) || {};
        setRows([{ name: "Cash", kind: "cash", amount: get("cash").amount || 0, drcr: get("cash").drcr || "dr" },
                 { name: "Bank", kind: "bank", amount: get("bank").amount || 0, drcr: get("bank").drcr || "dr" }]);
      });
    }
  }, [tab]);
  useEffect(() => { load(); setSel(0); }, [load]);
  const filtered = rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));
  useViewKeys({
    blocked, dialog: !!edit, count: filtered.length, sel, setSel,
    onEnter: (i) => filtered[i] && setEdit(filtered[i]),
    onEsc: onBack, onPeriod: onOpenPeriod, onPdf: () => {}, onXls: () => {},
  });
  const kindOf = (r) => (tab === "cashbank" ? r.kind : tab);
  const TabBtn = ({ k, lbl }) => (
    <button onClick={() => { setTab(k); setQ(""); }} data-testid={`ob-tab-${k}`}
      className={`px-3 py-1.5 text-xs font-bold rounded-sm ${tab === k ? "bg-[#0f766e] text-white" : "bg-white border border-[#d8ccae] text-slate-700"}`}>{lbl}</button>
  );
  return (
    <Frame title="Opening Balances (Books Beginning)"
      sub="Closing of last year auto-rolls into every FY · set pre-software balances here"
      chips={[["↑↓", "Move"], ["Enter", "Set Opening"], ["Type", "Search"], ["Esc", "Back"]]}
      sidebar={<>
        <FnBtn k="Esc" label="Back" onClick={onBack} testid="ob-fn-back" />
      </>}>
      <div className="p-3 flex gap-2 items-center border-b border-[#eee5cf] bg-[#fbf7ee]">
        <TabBtn k="party" lbl="Parties" />
        <TabBtn k="stock" lbl="Stock Items" />
        <TabBtn k="cashbank" lbl="Cash & Bank" />
        {tab !== "cashbank" && (
          <input value={q} onChange={(e) => { setQ(e.target.value); setSel(0); }} data-testid="ob-search"
            placeholder="Search…" className="ml-auto w-64 border border-[#d8ccae] rounded-sm px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0f766e]" />
        )}
      </div>
      <table className="w-full text-[13px]" data-testid="ob-table">
        <thead><tr className="bg-[#f6efdd] text-slate-700">
          <th className="px-3 py-1.5 font-bold text-left">Name</th>
          {tab === "stock"
            ? <><th className="px-3 py-1.5 font-bold text-right">Opening Qty</th><th className="px-3 py-1.5 font-bold text-right">Rate</th><th className="px-3 py-1.5 font-bold text-right">Value</th></>
            : <><th className="px-3 py-1.5 font-bold text-right">Opening Balance</th><th className="px-3 py-1.5 font-bold text-right w-20">Dr/Cr</th></>}
        </tr></thead>
        <tbody>
          {filtered.map((r, i) => (
            <tr key={r.name + i} data-testid={`ob-row-${i}`} onClick={() => setSel(i)} onDoubleClick={() => setEdit(r)}
              onMouseEnter={() => setSel(i)}
              className={`border-b border-[#f1ecdd] cursor-pointer ${sel === i ? "bg-[#0f766e] text-white" : "hover:bg-[#f6efdd]"}`}>
              <td className="px-3 py-1.5 font-medium">{r.name}
                {r.party_type ? <span className={`ml-2 text-[10px] uppercase ${sel === i ? "text-teal-100" : "text-slate-400"}`}>{r.party_type}</span> : null}
              </td>
              {tab === "stock" ? (
                <>
                  <td className="px-3 py-1.5 text-right tabular-nums">{r.qty ? fmt0(r.qty) : "—"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{r.rate ? fmt0(r.rate) : ""}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-bold">{r.value ? fmt0(r.value) : ""}</td>
                </>
              ) : (
                <>
                  <td className="px-3 py-1.5 text-right tabular-nums font-bold">{r.amount ? fmt0(r.amount) : "—"}</td>
                  <td className="px-3 py-1.5 text-right uppercase text-[11px] font-bold">{r.amount ? r.drcr : ""}</td>
                </>
              )}
            </tr>
          ))}
          {filtered.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">No entries</td></tr>}
        </tbody>
      </table>
      {edit && (
        <OBDialog row={edit} kind={kindOf(edit)} onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); load(); }} />
      )}
    </Frame>
  );
}

function AuditEditDialog({ section, onClose, onSave }) {
  const [text, setText] = useState(section.text);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
      if (e.key === "Enter" && (e.ctrlKey || e.altKey)) { e.preventDefault(); e.stopPropagation(); onSave(text); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, onSave, text]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" data-testid="audit-edit-dialog">
      <div className="bg-[#fbf7ee] border-2 border-[#0f766e] shadow-2xl w-full max-w-2xl">
        <div className="bg-[#0f766e] text-white px-3 py-1.5 text-xs font-bold tracking-widest uppercase">Edit — {section.title}</div>
        <div className="p-4 space-y-3">
          <textarea ref={ref} value={text} onChange={(e) => setText(e.target.value)} rows={14} data-testid="audit-edit-text"
            className="w-full border border-[#d8ccae] rounded-sm px-2 py-1.5 text-[13px] leading-relaxed bg-white focus:outline-none focus:border-[#0f766e] font-mono" />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1 text-xs font-bold border border-[#d8ccae] rounded-sm">Esc Cancel</button>
            <button onClick={() => onSave(text)} data-testid="audit-edit-save" className="px-3 py-1 text-xs font-bold bg-[#0f766e] text-white rounded-sm">Ctrl+Enter Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AuditReportView({ period, onBack, onOpenPeriod, blocked }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(0);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/tally/fin/audit-report`, { params: { frm: period.from, to: period.to } })
      .then((r) => setData(r.data)).catch(() => toast.error("Could not load the Audit Report"));
  }, [period]);
  useEffect(() => { load(); }, [load]);
  const sections = data?.sections || [];
  const doPdf = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tally/fin/audit-report/pdf`, {
        params: { frm: period.from, to: period.to }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_report_${period.to}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("PDF exported");
    } catch { toast.error("Export failed"); }
  }, [period]);
  const saveSection = useCallback(async (idx, text) => {
    const next = sections.map((s, i) => (i === idx ? { ...s, text } : s));
    try {
      await axios.put(`${API}/tally/fin/audit-report`, { frm: period.from, to: period.to, sections: next });
      toast.success("Section saved");
      setEdit(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  }, [sections, period, load]);
  const resetAll = useCallback(async () => {
    if (!window.confirm("Reset the whole report to auto-generated text?")) return;
    await axios.post(`${API}/tally/fin/audit-report/reset`, { frm: period.from, to: period.to, sections: [] });
    toast.success("Reset to auto");
    load();
  }, [period, load]);
  useViewKeys({
    blocked, dialog: !!edit, count: sections.length, sel, setSel,
    onEnter: (i) => sections[i] && setEdit({ ...sections[i], idx: i }),
    onEsc: onBack, onPeriod: onOpenPeriod, onPdf: doPdf, onXls: doPdf,
  });
  return (
    <Frame title="Independent Auditor's Report" sub={`${dshow(period.from)} to ${dshow(period.to)}${data?.edited ? " · EDITED" : " · AUTO"}`}
      chips={[["↑↓", "Move"], ["Enter", "Edit Section"], ["F2", "Period"], ["Alt+P", "PDF"], ["Esc", "Back"]]}
      sidebar={<>
        <FnBtn k="F2" label="Period" onClick={onOpenPeriod} testid="ar-fn-period" />
        <FnBtn k="Alt+P" label="Export PDF" onClick={doPdf} testid="ar-export-pdf" />
        <FnBtn k="R" label="Reset to Auto" onClick={resetAll} testid="ar-reset" />
        <FnBtn k="Esc" label="Back" onClick={onBack} testid="ar-fn-back" />
      </>}>
      <div className="p-4 space-y-3 max-w-4xl" data-testid="fin-audit-report">
        {sections.map((s, i) => (
          <div key={s.key} data-testid={`ar-section-${s.key}`} onClick={() => setSel(i)}
            onDoubleClick={() => setEdit({ ...s, idx: i })} onMouseEnter={() => setSel(i)}
            className={`border rounded-sm cursor-pointer ${sel === i ? "border-[#0f766e] ring-1 ring-[#0f766e]" : "border-[#e7dcc3]"}`}>
            <div className={`px-3 py-1 text-[11px] font-bold uppercase tracking-widest flex justify-between ${sel === i ? "bg-[#0f766e] text-white" : "bg-[#f6efdd] text-[#0f766e]"}`}>
              {s.title}
              <span className="opacity-70 normal-case tracking-normal">Enter to edit</span>
            </div>
            <div className="px-3 py-2 text-[13px] leading-relaxed text-slate-800 whitespace-pre-wrap bg-white">{s.text}</div>
          </div>
        ))}
      </div>
      {edit && (
        <AuditEditDialog section={edit} onClose={() => setEdit(null)} onSave={(t) => saveSection(edit.idx, t)} />
      )}
    </Frame>
  );
}
